import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AsmService } from '@spartacus/asm/core';
import { AsmAuthStorageService, TokenTarget } from '@spartacus/asm/root';

import {
  AuthActions,
  AuthService,
  AuthToken,
  OAuthLibWrapperService,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  UserIdService,
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';


/**
 * Auth service for CS agent. Useful to login/logout agent, start emulation
 * or get information about the status of emulation.
 */
@Injectable({
  providedIn: 'root',
})
export class KnBrCsAgentAuthService {
  private agentTokenBackup: any = null;

  constructor(
    protected authService: AuthService,
    protected authStorageService: AsmAuthStorageService,
    protected userIdService: UserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected store: Store,
    protected userService: UserAccountFacade,
    protected asmService: AsmService
  ) {}

  /**
   * Loads access token for a customer support agent.
   * @param userId
   * @param password
   */
  async authorizeCustomerSupportAgent(userId: string, password: string): Promise<void> {
    let userToken: AuthToken | undefined;
    this.authStorageService
      .getToken()
      .subscribe((token) => (userToken = token))
      .unsubscribe();

    this.authStorageService.switchTokenTargetToCSAgent();
    try {
      await this.oAuthLibWrapperService.authorizeWithPasswordFlow(userId, password);
      // Start emulation for currently logged in user
      let customerId: string | undefined;
      this.userService
        .get()
        .subscribe((user) => (customerId = user?.customerId))
        .unsubscribe();
      this.store.dispatch(new AuthActions.Logout());

      if (customerId !== undefined && userToken !== undefined) {
        // OCC specific user id handling. Customize when implementing different backend
        this.userIdService.setUserId(customerId);
        this.authStorageService.setEmulatedUserToken(userToken);
        this.store.dispatch(new AuthActions.Login());
      } else {
        // When we can't get the customerId just end all current sessions
        this.userIdService.setUserId(OCC_USER_ID_ANONYMOUS);
        this.authStorageService.clearEmulatedUserToken();
      }
    } catch {
      this.authStorageService.switchTokenTargetToUser();
    }
  }

  public handleOAuthCallbackIfActive(): void {
    const asmEnabled = localStorage.getItem('asm_enabled') === 'true';
    const isNormalCustomer = this.isNormalCustomerClient();

    if (asmEnabled && !isNormalCustomer) {
      this.authStorageService.setTokenTarget(TokenTarget.CSAgent);
      this.authStorageService.switchTokenTargetToCSAgent();
    }
  }

  async authorizeCustomerSupportAgentWhenUseCodeFlow(): Promise<void> {
    sessionStorage.setItem('asm_mode', 'true');
    this.authStorageService.switchTokenTargetToCSAgent();
    this.authStorageService.setTokenTarget(TokenTarget.CSAgent);

    try {
      await this.oAuthLibWrapperService.initLoginFlow();
    } catch (error) {
      this.authStorageService.switchTokenTargetToUser();
      throw error;
    }
  }

  /**
   * Starts an ASM customer emulation session.
   * A customer emulation session is stopped by calling logout().
   * @param customerId
   */
  public startCustomerEmulationSession(customerId: string): void {
    this.authStorageService.clearEmulatedUserToken();

    // OCC specific user id handling. Customize when implementing different backend
    this.store.dispatch(new AuthActions.Logout());
    this.userIdService.setUserId(customerId);
    this.store.dispatch(new AuthActions.Login());
  }

  public initCsAgentContext(): void {
    this.authService.isUserLoggedIn().pipe(
      filter(Boolean),
      take(1)
    ).subscribe(() => {
      this.authStorageService.getTokenTarget().pipe(take(1)).subscribe((currentTarget) => {
        if (currentTarget === TokenTarget.CSAgent) {
          return;
        }
        this.authStorageService.setTokenTarget(TokenTarget.CSAgent);
        this.authStorageService.switchTokenTargetToCSAgent();
        this.store.dispatch(new AuthActions.Login());
      });
    });
  }

  public endCustomerEmulationSession(): void {
    this.store.dispatch(new AuthActions.Logout());
    this.userIdService.setUserId(OCC_USER_ID_ANONYMOUS);
    this.store.dispatch(new AuthActions.Login());
  }

  /**
   * Check if CS agent is currently logged in.
   *
   * @returns observable emitting true when CS agent is logged in or false when not.
   */
  public isCustomerSupportAgentLoggedIn(): Observable<boolean> {
    return combineLatest([
      this.authStorageService.getToken(),
      this.authStorageService.getTokenTarget()
    ]).pipe(
      map(([token, tokenTarget]) =>
        this.isNormalCustomerClient()
          ? false
          : Boolean(token?.access_token && tokenTarget === TokenTarget.CSAgent)
      )
    );
  }

  /**
   * Utility function to determine if customer is emulated.
   *
   * @returns observable emitting true when there is active emulation session or false when not.
   */
  public isCustomerEmulated(): Observable<boolean> {
    return this.userIdService.isEmulated();
  }

  /**
   * Returns the customer support agent's token loading status
   */
  public getCustomerSupportAgentTokenLoading(): Observable<boolean> {
    // TODO(#8248): Create new loading state outside of store
    return of(false);
  }

  public restoreAgentSessionContext(): void {
    const backupStr = localStorage.getItem('knbr_backup_agent_token');
    const tokenToRestore = this.agentTokenBackup || (backupStr ? JSON.parse(backupStr) : null);

    if (tokenToRestore) {
      // 1. Shift target token scope back to Agent
      this.authStorageService.setTokenTarget(TokenTarget.CSAgent);
      this.authStorageService.switchTokenTargetToCSAgent();

      // 2. Inject the backup agent token back into active memory
      this.authStorageService.setToken(tokenToRestore);
      this.userIdService.setUserId(OCC_USER_ID_CURRENT);

      // 3. Sync NgRx Store context status
      this.store.dispatch(new AuthActions.Login());

      // 4. Clean up local state references
      this.agentTokenBackup = null;
      localStorage.removeItem('knbr_backup_agent_token');
    }
  }

  async logoutCustomerSupportAgent(): Promise<void> {
    this.agentTokenBackup = null;
    localStorage.removeItem('knbr_backup_agent_token');
    await this.oAuthLibWrapperService.revokeAndLogout();
    this.store.dispatch(new AuthActions.Logout());
      this.authService.logout();
    }

  private isNormalCustomerClient(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('client_id');
    const authClientId = environment.AUTHENTICATION_CLIENT_ID;

    return (
      clientId === authClientId ||
      window.location.href.includes(`client_id=${authClientId}`)
    );
  }
}
